import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import freshroadlogo from '../images/freshroadlogo.png';
import '../pages/LandingPage.css';
import LandingPageDemo from '../components/LandingPageDemo';
import {
  Mic,
  Globe,
  Zap,
  BarChart3,
  BookOpen,
  Rocket,
  MessageCircle,
  Phone,
  Settings,
  Users,
  Lock,
  TrendingUp,
  ArrowRight,
  CheckCircle,
  MessageSquare,
  Mail,
  Smartphone,
  DollarSign
} from 'lucide-react';

const pricingTiers = [
  {
    id: 'basic',
    name: 'Basic',
    description: 'Perfect for getting started',
    pricePerHour: 0.50,
    featured: false,
    features: [
      'Limited hours',
      'Up to 5 AI Agents',
      '5 Knowledge Bases',
      'Inbound/Outbound Calls',
      'Limited Voice Options',
      'Basic Analytics & Logs'
    ]
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'Most popular - almost all features',
    pricePerHour: 0.90,
    featured: true,
    features: [
      'Better hours',
      'Up to 10 AI Agents',
      'Up to 10 Knowledge Bases',
      'Inbound/Outbound/SMS/Web Widget',
      'Advanced Analytics',
      'Priority Support'
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Everything you need',
    custom: true,
    featured: false,
    features: [
      'Unlimited hours',
      'Unlimited AI Agents',
      'Unlimited Knowledge Bases',
      'All channels (Phone/SMS/Chat/Web)',
      'Advanced Analytics & Custom Reports',
      'Custom Integrations',
      'Dedicated Account Manager',
      '24/7 Dedicated Support'
    ]
  }
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [hours, setHours] = useState(1000);
  const [selectedTier, setSelectedTier] = useState('professional');

  useEffect(() => {
    // Redirect authenticated users to dashboard
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(id);
    }
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="landing-nav-container">
          <Link to="/" className="landing-logo">
            <img src={freshroadlogo} alt="AI Call Center" />
          </Link>

          {!isMobile && (
            <div className="landing-nav-links">
              <button onClick={() => scrollToSection('features')} className="nav-link">
                Features
              </button>
              <button onClick={() => scrollToSection('capabilities')} className="nav-link">
                Capabilities
              </button>
              <button onClick={() => scrollToSection('pricing')} className="nav-link">
                Pricing
              </button>
            </div>
          )}

          <div className="landing-nav-actions">
            <Link to="/login" className="landing-btn-secondary">
              Sign In
            </Link>
            <Link to="/register" className="landing-btn-primary">
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <div className="landing-hero-text">
            <h1 className="landing-h1">
              AI Voice Agents for Every Business
            </h1>
            <p className="landing-subtitle">
              Build, customize, and deploy intelligent voice agents in minutes. Automate customer support, sales, and surveys with AI that understands context and languages.
            </p>
            <div className="landing-hero-ctas">
              <Link to="/register" className="landing-btn-primary landing-btn-lg">
                Start Free <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <button className="landing-btn-secondary landing-btn-lg" onClick={() => scrollToSection('how-it-works')}>
                See How It Works
              </button>
            </div>
            <p className="landing-hero-note">
              <CheckCircle className="w-4 h-4 inline mr-1" />
              No credit card required. Start building in minutes.
            </p>
          </div>
          <div className="landing-hero-visual">
            <div className="landing-hero-card">
              <div className="landing-hero-card-inner">
                <div className="landing-animation-element"></div>
                <div className="landing-animation-element landing-animation-element-2"></div>
                <Mic className="w-24 h-24 text-landing-primary opacity-20" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Showcase - Features */}
      <section id="features" className="landing-section landing-features">
        <div className="landing-section-container">
          <div className="landing-section-header">
            <h2 className="landing-h2">Powerful Features Built for Automation</h2>
            <p className="landing-section-subtitle">Everything you need to build and deploy intelligent voice agents</p>
          </div>

          <div className="landing-features-grid">
            {/* Feature 1: AI Voice Conversations */}
            <div className="landing-feature-card">
              <div className="landing-feature-icon landing-feature-icon-1">
                <Mic className="w-8 h-8" />
              </div>
              <h3>AI Voice Conversations</h3>
              <p>Enable natural, context-aware conversations. Handle inbound calls, outbound campaigns, and surveys with human-like interactions.</p>
              <div className="landing-feature-points">
                <div className="landing-feature-point">
                  <CheckCircle className="w-4 h-4" />
                  <span>Inbound & Outbound Calls</span>
                </div>
                <div className="landing-feature-point">
                  <CheckCircle className="w-4 h-4" />
                  <span>24/7 Availability</span>
                </div>
              </div>
            </div>

            {/* Feature 2: Multilingual Support */}
            <div className="landing-feature-card">
              <div className="landing-feature-icon landing-feature-icon-2">
                <Globe className="w-8 h-8" />
              </div>
              <h3>Multilingual Support</h3>
              <p>Automatically detect and switch between 15+ languages. Serve global customers in their native language with automatic language routing.</p>
              <div className="landing-feature-points">
                <div className="landing-feature-point">
                  <CheckCircle className="w-4 h-4" />
                  <span>Auto Language Detection</span>
                </div>
                <div className="landing-feature-point">
                  <CheckCircle className="w-4 h-4" />
                  <span>15+ Language Support</span>
                </div>
              </div>
            </div>

            {/* Feature 3: Visual Flow Builder */}
            <div className="landing-feature-card">
              <div className="landing-feature-icon landing-feature-icon-3">
                <Zap className="w-8 h-8" />
              </div>
              <h3>Visual Flow Builder</h3>
              <p>Drag-and-drop conversation flows with no code. Create complex routing logic, conditional responses, and dynamic interactions visually.</p>
              <div className="landing-feature-points">
                <div className="landing-feature-point">
                  <CheckCircle className="w-4 h-4" />
                  <span>Drag & Drop Editor</span>
                </div>
                <div className="landing-feature-point">
                  <CheckCircle className="w-4 h-4" />
                  <span>Complex Logic Support</span>
                </div>
              </div>
            </div>

            {/* Feature 4: Real-time Analytics */}
            <div className="landing-feature-card">
              <div className="landing-feature-icon landing-feature-icon-4">
                <BarChart3 className="w-8 h-8" />
              </div>
              <h3>Real-time Analytics</h3>
              <p>Monitor every call as it happens. Track performance, transcripts, sentiment, and custom metrics with instant insights.</p>
              <div className="landing-feature-points">
                <div className="landing-feature-point">
                  <CheckCircle className="w-4 h-4" />
                  <span>Live Call Monitoring</span>
                </div>
                <div className="landing-feature-point">
                  <CheckCircle className="w-4 h-4" />
                  <span>Call Analytics & Logs</span>
                </div>
              </div>
            </div>

            {/* Feature 5: Knowledge Base Integration */}
            <div className="landing-feature-card">
              <div className="landing-feature-icon landing-feature-icon-5">
                <BookOpen className="w-8 h-8" />
              </div>
              <h3>Knowledge Base Integration</h3>
              <p>Ground AI responses in your own data. Connect documents, FAQs, and custom databases for accurate, contextualized answers.</p>
              <div className="landing-feature-points">
                <div className="landing-feature-point">
                  <CheckCircle className="w-4 h-4" />
                  <span>Document Upload</span>
                </div>
                <div className="landing-feature-point">
                  <CheckCircle className="w-4 h-4" />
                  <span>Context-Aware Responses</span>
                </div>
              </div>
            </div>

            {/* Feature 6: Easy Deployment */}
            <div className="landing-feature-card">
              <div className="landing-feature-icon landing-feature-icon-6">
                <Rocket className="w-8 h-8" />
              </div>
              <h3>Easy Deployment</h3>
              <p>Deploy in seconds. Connect phone lines, websites, or messaging apps. One-click activation with no infrastructure needed.</p>
              <div className="landing-feature-points">
                <div className="landing-feature-point">
                  <CheckCircle className="w-4 h-4" />
                  <span>Phone Integration</span>
                </div>
                <div className="landing-feature-point">
                  <CheckCircle className="w-4 h-4" />
                  <span>Multiple Channels</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="landing-section landing-how-it-works">
        <div className="landing-section-container">
          <div className="landing-section-header">
            <h2 className="landing-h2">How It Works in 3 Steps</h2>
            <p className="landing-section-subtitle">From design to deployment in minutes</p>
          </div>

          <div className="landing-steps">
            {/* Step 1 */}
            <div className="landing-step">
              <div className="landing-step-number">1</div>
              <div className="landing-step-icon">
                <Settings className="w-12 h-12" />
              </div>
              <h3>Design Your Flow</h3>
              <p>Use the visual flow builder to create conversational logic. Add branches, conditions, knowledge base lookups, and integrations.</p>
            </div>

            <div className="landing-step-connector"></div>

            {/* Step 2 */}
            <div className="landing-step">
              <div className="landing-step-number">2</div>
              <div className="landing-step-icon">
                <Rocket className="w-12 h-12" />
              </div>
              <h3>Deploy Instantly</h3>
              <p>Connect your phone number or channel. The AI agent goes live immediately and starts handling interactions 24/7.</p>
            </div>

            <div className="landing-step-connector"></div>

            {/* Step 3 */}
            <div className="landing-step">
              <div className="landing-step-number">3</div>
              <div className="landing-step-icon">
                <TrendingUp className="w-12 h-12" />
              </div>
              <h3>Analyze & Optimize</h3>
              <p>Monitor live calls, review transcripts, and analyze performance metrics. Continuously improve your agent with real data.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Demo Section */}
      <section className="landing-demo-section">
        <div className="landing-section-container">
          <div className="landing-demo-section-header">
            <h2 className="landing-h2">See It In Action</h2>
            <p className="landing-section-subtitle">Try our AI receptionist demo - no login required</p>
          </div>
          <LandingPageDemo />
        </div>
      </section>

      {/* Capabilities Grid */}
      <section id="capabilities" className="landing-section landing-capabilities">
        <div className="landing-section-container">
          <div className="landing-section-header">
            <h2 className="landing-h2">Enterprise-Grade Capabilities</h2>
            <p className="landing-section-subtitle">Built for serious businesses and complex use cases</p>
          </div>

          <div className="landing-capabilities-grid">
            {[
              { icon: <Zap />, title: 'Conversation Flow Builder', desc: 'Visual, drag-and-drop workflow editor', color: 'primary' },
              { icon: <Phone />, title: 'Inbound Call Routing', desc: 'Smart routing based on intent and skills', color: 'blue' },
              { icon: <MessageCircle />, title: 'Outbound Campaigns', desc: 'Batch calling with scheduling and personalization', color: 'teal' },
              { icon: <Globe />, title: 'Language Detection', desc: 'Auto-detect and switch between languages', color: 'sky' },
              { icon: <BookOpen />, title: 'Knowledge Base Retrieval', desc: 'Ground responses in your own documents', color: 'primary' },
              { icon: <BarChart3 />, title: 'Custom Analytics', desc: 'Build dashboards for your metrics', color: 'blue' },
              { icon: <Users />, title: 'Multi-user Collaboration', desc: 'Teams can work together on flows', color: 'teal' },
              { icon: <Lock />, title: 'Recording & Compliance', desc: 'Automatic recording and transcription', color: 'sky' }
            ].map((cap, idx) => (
              <div key={idx} className={`landing-capability-item landing-capability-color-${cap.color}`}>
                <div className="landing-capability-header">
                  <div className="landing-capability-icon">{cap.icon}</div>
                  <div className="landing-capability-accent"></div>
                </div>
                <h4>{cap.title}</h4>
                <p>{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Savings Section */}
      <section className="landing-section landing-cost-savings">
        <div className="landing-section-container">
          <div className="landing-section-header">
            <h2 className="landing-h2">Massive Cost Savings</h2>
            <p className="landing-section-subtitle">AI-powered automation dramatically reduces operational costs</p>
          </div>

          <div className="landing-cost-comparison">
            <div className="landing-cost-item">
              <div className="landing-cost-header">Traditional Call Center</div>
              <div className="landing-cost-amount">$150,000</div>
              <div className="landing-cost-subtext">For 10,000 hours annually</div>
              <ul className="landing-cost-list">
                <li>
                  <CheckCircle className="w-5 h-5" />
                  <span>Human agent salary & benefits</span>
                </li>
                <li>
                  <CheckCircle className="w-5 h-5" />
                  <span>Training and management</span>
                </li>
                <li>
                  <CheckCircle className="w-5 h-5" />
                  <span>Infrastructure & systems</span>
                </li>
                <li>
                  <CheckCircle className="w-5 h-5" />
                  <span>24/7 staffing requirements</span>
                </li>
              </ul>
            </div>

            <div className="landing-cost-vs">VS</div>

            <div className="landing-cost-item landing-cost-item-ai">
              <div className="landing-cost-header landing-cost-header-ai">AI Call Center</div>
              <div className="landing-cost-amount landing-cost-amount-ai">$9,000</div>
              <div className="landing-cost-subtext">For 10,000 hours annually</div>
              <ul className="landing-cost-list">
                <li>
                  <CheckCircle className="w-5 h-5" />
                  <span>AI service usage</span>
                </li>
                <li>
                  <CheckCircle className="w-5 h-5" />
                  <span>Automatic updates & maintenance</span>
                </li>
                <li>
                  <CheckCircle className="w-5 h-5" />
                  <span>Cloud infrastructure included</span>
                </li>
                <li>
                  <CheckCircle className="w-5 h-5" />
                  <span>24/7 always available</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="landing-savings-stat">
            <div className="landing-savings-badge">
              <DollarSign className="w-6 h-6" />
              <span>94% Cost Reduction</span>
            </div>
            <p>Save $141,000 annually while improving service quality and availability</p>
          </div>
        </div>
      </section>

      {/* Omni-Channel Communication Section */}
      <section className="landing-section landing-omni-channel">
        <div className="landing-section-container">
          <div className="landing-section-header">
            <h2 className="landing-h2">True Omni-Channel Communication</h2>
            <p className="landing-section-subtitle">Meet customers on their preferred channels</p>
          </div>

          <div className="landing-omni-showcase">
            <div className="landing-omni-card landing-omni-card-1">
              <div className="landing-omni-card-icon">
                <Phone className="w-8 h-8" />
              </div>
              <h3>Voice Call</h3>
              <p>Deliver natural, human-like phone conversations at scale.</p>
            </div>

            <div className="landing-omni-card landing-omni-card-2">
              <div className="landing-omni-card-icon">
                <MessageSquare className="w-8 h-8" />
              </div>
              <h3>Chat</h3>
              <p>Deploy AI-powered conversations across web and in-app chat experiences.</p>
            </div>

            <div className="landing-omni-card landing-omni-card-3">
              <div className="landing-omni-card-icon">
                <Smartphone className="w-8 h-8" />
              </div>
              <h3>SMS</h3>
              <p>Engage customers through reliable, compliant text messaging.</p>
            </div>

            <div className="landing-omni-card landing-omni-card-4">
              <div className="landing-omni-card-icon">
                <Settings className="w-8 h-8" />
              </div>
              <h3>API</h3>
              <p>Integrate AI agents into any custom application or workflow.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="landing-section landing-pricing">
        <div className="landing-section-container">
          <div className="landing-section-header">
            <h2 className="landing-h2">Simple, Transparent Pricing</h2>
            <p className="landing-section-subtitle">Pay only for what you use. No setup fees, no hidden charges.</p>
          </div>

          {/* Pricing Calculator */}
          <div className="landing-pricing-calculator">
            <h3 className="landing-pricing-calc-title">Pricing Calculator</h3>
            <p className="landing-pricing-calc-subtitle">See your costs instantly</p>

            {/* Tier Selection */}
            <div className="landing-calc-tier-selector">
              {pricingTiers.map((tier) => (
                <button
                  key={tier.id}
                  onClick={() => setSelectedTier(tier.id)}
                  className={`landing-calc-tier-btn ${selectedTier === tier.id ? 'landing-calc-tier-btn-active' : ''}`}
                >
                  <div className="landing-calc-tier-name">{tier.name}</div>
                  {tier.pricePerHour && <div className="landing-calc-tier-price">${tier.pricePerHour.toFixed(2)}/hr</div>}
                  {tier.custom && <div className="landing-calc-tier-price">Custom</div>}
                </button>
              ))}
            </div>

            {/* Hours Input */}
            {!pricingTiers.find(t => t.id === selectedTier)?.custom && (
              <div className="landing-calc-hours-section">
                <div className="landing-calc-hours-header">
                  <label>How many hours per month?</label>
                  <div className="landing-calc-hours-display">{hours.toLocaleString()} hours</div>
                </div>

                <input
                  type="range"
                  min="100"
                  max="100000"
                  value={hours}
                  onChange={(e) => setHours(parseInt(e.target.value))}
                  className="landing-calc-slider-large"
                />

                <div className="landing-calc-hours-footer">
                  <input
                    type="number"
                    value={hours}
                    onChange={(e) => setHours(Math.max(100, parseInt(e.target.value) || 0))}
                    className="landing-calc-number-input-large"
                    min="100"
                  />
                </div>
              </div>
            )}

            {/* Results */}
            <div className="landing-pricing-calc-results-new">
              {pricingTiers.find(t => t.id === selectedTier)?.custom ? (
                <div className="landing-calc-result-enterprise">
                  <h4>Enterprise Pricing</h4>
                  <p>Custom pricing based on your organization's needs</p>
                  <a href="mailto:sales@freshroadhealth.com" className="landing-calc-cta-btn">
                    Contact Sales for Quote
                  </a>
                </div>
              ) : (
                <>
                  <div className="landing-calc-result-row">
                    <div className="landing-calc-result-col">
                      <div className="landing-calc-result-label">Monthly Cost</div>
                      <div className="landing-calc-result-amount">
                        ${(hours * pricingTiers.find(t => t.id === selectedTier)?.pricePerHour).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                    <div className="landing-calc-result-col">
                      <div className="landing-calc-result-label">Annual Cost</div>
                      <div className="landing-calc-result-amount">
                        ${(hours * pricingTiers.find(t => t.id === selectedTier)?.pricePerHour * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>

                  <div className="landing-calc-savings-banner">
                    <div className="landing-calc-savings-text">
                      <strong>Savings vs Traditional Call Center</strong>
                      <p>${(150000 - (hours * pricingTiers.find(t => t.id === selectedTier)?.pricePerHour * 12)).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} per year</p>
                    </div>
                    <div className="landing-calc-savings-badge">
                      {Math.round(((150000 - (hours * pricingTiers.find(t => t.id === selectedTier)?.pricePerHour * 12)) / 150000) * 100)}%
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Pricing Plans */}
          <div className="landing-pricing-plans">
            {pricingTiers.map((tier) => (
              <div
                key={tier.id}
                className={`landing-pricing-plan ${tier.featured ? 'landing-pricing-plan-featured' : ''}`}
              >
                {tier.featured && <div className="landing-plan-badge">Most Popular</div>}
                <h3 className="landing-plan-name">{tier.name}</h3>
                <p className="landing-plan-desc">{tier.description}</p>
                <div className="landing-plan-price">
                  {tier.custom ? 'Custom' : `$${tier.pricePerHour.toFixed(2)}`}
                  {!tier.custom && <span className="landing-plan-period">/hour</span>}
                </div>
                <ul className="landing-plan-features">
                  {tier.features.map((feature, idx) => (
                    <li key={idx}><CheckCircle className="w-4 h-4" /> {feature}</li>
                  ))}
                </ul>
                {tier.custom ? (
                  <a href="mailto:sales@freshroadhealth.com" className="landing-plan-btn landing-plan-btn-secondary">
                    Contact Sales
                  </a>
                ) : (
                  <Link to="/register" className={`landing-plan-btn ${tier.featured ? 'landing-plan-btn-primary' : 'landing-plan-btn-secondary'}`}>
                    {tier.featured ? 'Start Free Trial' : 'Get Started'} {tier.featured && <ArrowRight className="w-4 h-4 ml-1" />}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof / Use Cases */}
      <section className="landing-section landing-use-cases">
        <div className="landing-section-container">
          <div className="landing-section-header">
            <h2 className="landing-h2">Use Cases</h2>
            <p className="landing-section-subtitle">Perfect for any industry and business size</p>
          </div>

          <div className="landing-use-cases-grid">
            {[
              { title: 'Customer Support', desc: 'Handle support tickets and FAQs 24/7 with AI agents.' },
              { title: 'Sales & Lead Gen', desc: 'Qualify leads and conduct initial sales conversations automatically.' },
              { title: 'Surveys & Feedback', desc: 'Collect customer feedback at scale with natural conversations.' },
              { title: 'Government Services', desc: 'Guide citizens through services in their preferred language.' },
              { title: 'Healthcare', desc: 'Appointment scheduling, symptom checking, patient surveys.' },
              { title: 'Finance', desc: 'Loan applications, account support, compliance workflows.' }
            ].map((useCase, idx) => (
              <div key={idx} className="landing-use-case-card">
                <h4>{useCase.title}</h4>
                <p>{useCase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-section landing-final-cta">
        <div className="landing-section-container">
          <div className="landing-cta-content">
            <h2 className="landing-h2">Ready to Build Your AI Voice Agent?</h2>
            <p className="landing-cta-subtitle">Get started free. No credit card required. Start handling calls in minutes.</p>

            <div className="landing-final-ctas">
              <Link to="/register" className="landing-btn-primary landing-btn-lg landing-btn-xl">
                Create Free Account <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/login" className="landing-btn-secondary landing-btn-lg">
                Already have an account? Sign In
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-container">
          <div className="landing-footer-content">
            <div className="landing-footer-section">
              <h4>Product</h4>
              <ul>
                <li><a href="#features">Features</a></li>
                <li><a href="#how-it-works">How It Works</a></li>
                <li><a href="#capabilities">Capabilities</a></li>
              </ul>
            </div>

            <div className="landing-footer-section">
              <h4>Company</h4>
              <ul>
                <li><a href="#">About</a></li>
                <li><a href="#">Blog</a></li>
                <li><button onClick={() => scrollToSection('pricing')} className="landing-footer-link">Pricing</button></li>
              </ul>
            </div>

            <div className="landing-footer-section">
              <h4>Resources</h4>
              <ul>
                <li><a href="#">Documentation</a></li>
                <li><a href="#">API</a></li>
                <li><a href="#">Support</a></li>
              </ul>
            </div>

            <div className="landing-footer-section">
              <h4>Legal</h4>
              <ul>
                <li><a href="#">Privacy</a></li>
                <li><a href="#">Terms</a></li>
                <li><a href="#">Security</a></li>
              </ul>
            </div>
          </div>

          <div className="landing-footer-bottom">
            <p>&copy; 2024 AI Call Center. All rights reserved. Powered by Fresh Road.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
